/**
 * Writes a text string to the system clipboard.
 *
 * @async
 * @param {string} data text to be written to the clipboard
 * @throws {Error} unable to copy text to clipboard
 */
export const copyTextToClipboard = async data => {
  const text =
    typeof data === 'object' && data !== null
      ? JSON.stringify(data, null, 2)
      : String(data ?? '');

  const fail = reason => {
    throw new Error(`Unable to copy text to clipboard: ${reason}`);
  };

  let reason = 'Clipboard API unavailable';

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      reason = error?.message || 'Clipboard write failed';
    }
  }

  // O fallback só existe em navegadores que ainda expõem execCommand. Sem ele,
  // devolvemos o motivo real em vez de deixar vazar um TypeError do navegador.
  if (typeof document.execCommand !== 'function') fail(reason);

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.cssText =
    'position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (error) {
    reason = error?.message || reason;
  } finally {
    document.body.removeChild(textArea);
  }

  if (!success) fail(reason);
};

/**
 * Handles OTP paste events by extracting numeric digits from clipboard data.
 *
 * @param {ClipboardEvent} event - The paste event from the clipboard
 * @param {number} maxLength - Maximum number of digits to extract (default: 6)
 * @returns {string|null} - Extracted numeric string or null if invalid
 */
export const handleOtpPaste = (event, maxLength = 6) => {
  if (!event?.clipboardData) return null;

  const pastedData = event.clipboardData
    .getData('text')
    .replace(/\D/g, '') // Remove all non-digit characters
    .slice(0, maxLength); // Limit to maxLength digits

  // Only return if we have the exact expected length
  return pastedData.length === maxLength ? pastedData : null;
};
